import _ from 'lodash';
import WixEvent from '../events/WixEvent';

/**
 * @callback $w.KeyboardEventHandler
 * @summary Handles events fired when the keyboard is pressed.
 * @param {$w.KeyboardEvent} event The keyboard event that occurred.
 * @param {$w.$w} $w Deprecated: A selector function. The $w function was used to
 *  enable event handlers to work with elements in [repeaters]($w.Repeater.html). Now, to get
 *  a scoped selector for working with repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`.
 */

/**
 * @class KeyboardEvent
 * @summary Event that is fired when the keyboard is pressed.
 * @description
 *  A `KeyboardEvent` event occurs when the user presses a key while the cursor
 *  is located inside a [`TextInput`]($w.TextInput.html), [`TextBox`]($w.TextBox.html),
 *  or [`RichTextBox`]($w.RichTextBox.html).
 * @memberof $w
 * @mixes $w.Event
 */
class KeyboardEvent extends WixEvent {
    constructor(target, event) {
        super(target, event);

        this.metaKey = _.get(event, 'metaKey');
        this.altKey = _.get(event, 'altKey');
        this.shiftKey = _.get(event, 'shiftKey');
        this.ctrlKey = _.get(event, 'ctrlKey');
        this.key = _.get(event, 'key');
        this.code = _.get(event, 'keyCode');
    }


    /**
     * @member {external:Boolean} metaKey
     * @label property
     * @syntax
     * get metaKey(): boolean
     * @summary Indicates if the ⌘ Command key on a Mac or ⊞ Windows key on a PC
     *  was pressed.
     * @snippet [KeyboardEvent-metaKey.es6=Get the keyboard event's `metaKey` property]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     *  This example gets the properties of a keyboard event fired when the
     *  `Shift` and `a` keys were pressed.
     * @memberof $w.KeyboardEvent
     * @readonly
     */


    /**
     * @member {external:Boolean} altKey
     * @label property
     * @syntax
     * get altKey(): boolean
     * @summary Indicates if the Option key on a Mac or Alt key on a PC was pressed.
     * @snippet [KeyboardEvent-altKey.es6=Get the keyboard event's `altKey` property]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     *  This example gets the properties of a keyboard event fired when the
     *  `Shift` and `a` keys were pressed.
     * @memberof $w.KeyboardEvent
     * @readonly
     */


    /**
     * @member {external:Boolean} shiftKey
     * @label property
     * @syntax
     * get shiftKey(): boolean
     * @summary Indicates if the Shift key was pressed.
     * @snippet [KeyboardEvent-shiftKey.es6=Get the keyboard event's `shiftKey` property]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     *  This example gets the properties of a keyboard event fired when the
     *  `Shift` and `a` keys were pressed.
     * @memberof $w.KeyboardEvent
     * @readonly
     */


    /**
     * @member {external:Boolean} ctrlKey
     * @label property
     * @syntax
     * get ctrlKey(): boolean
     * @summary Indicates if the Control (Ctrl) key was pressed.
     * @snippet [KeyboardEvent-ctrlKey.es6=Get the keyboard event's `ctrlKey` property]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     *  This example gets the properties of a keyboard event fired when the
     *  `Shift` and `a` keys were pressed.
     * @memberof $w.KeyboardEvent
     * @readonly
     */


    /**
     * @member {external:String} key
     * @label property
     * @syntax
     * get key(): string
     * @summary Gets a string representation of the key that was pressed.
     * @description
     *  For keys representing a printable character, the `key` property gets a 
     *  Unicode character string containing the printable representation of the 
     *  key that was pressed. This does not necessarily correspond to what is
     *  printed on the physical key since the user could have changed the keyboard 
     *  layout.
     *
     *  For keys that have no printable representation, a string indicating the
     *  key's purpose is returned. Some examples are: 
     * 
     *  + `"Backspace"`
     *  + `"Enter"`
     *  + `"F1"`
     *  + `"Escape"`
     *  + `"Tab"`
     *
     * @snippet [KeyboardEvent-key.es6=Get the keyboard event's `key` property]
     * @snippet [KeyboardEvent-all.es6=Get all of the keyboard event's properties]
     *  This example get the properties of a keyboard event fired when the
     *  `Shift` and `a` keys were pressed.
     * @memberof $w.KeyboardEvent
     * @readonly
     */

    /* REMOVING THIS FOR NOW; IT'S BASICALLY USELESS AS IT'S COMPLETELY BROWSER-DEPENDENT
     * @member {external:String} code
     * @label property
     * @syntax let charCode = event.code;
     * @summary Gets the numeric Unicode value of the character that was pressed.
     * @description
     *  The `code` property gets the ASCII code of the
     *  pressed key's physical representation, regardless of the current keyboard layout. For example, if
     *  the keyboard layout is set to Spanish on a US keyboard, pressing "ñ" results in a
     *  `code` value of 186. This is the ASCII code for a semicolon, since that is what is printed on the key.
     *
     *  For the exceptions where a [non-printable keypress](#non-printable) generates a `KeyboardEvent` event:
     *  * If the Enter key is pressed, `code` returns 13.
     *  * If the Escape key is pressed, `code` returns 27.
     *  * If more than one key is pressed and the combination includes a modifier that makes the resulting keystroke
     *    non-printing, the returned value is the printable character. For example, if the non-printing combination
     *    Ctrl-A is pressed, `code` returns the letter "a".
     *
     * @see [#key](#key)
     * @memberof $w.KeyboardEvent
     * @readonly
     */

}

export default KeyboardEvent;
